import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { DollarSign, Calendar, Percent, Clock } from 'lucide-react';

const LoanCalculator = () => {
  const { t, i18n } = useTranslation();
  const [loanAmount, setLoanAmount] = useState(500);
  const [paymentFrequency, setPaymentFrequency] = useState('biweekly'); // 'weekly' or 'biweekly'
  const [interestRate] = useState(24); // Fixed rate: 24% APR
  const [payment, setPayment] = useState(0);
  const [loanTerm, setLoanTerm] = useState(3);
  const [numberOfPayments, setNumberOfPayments] = useState(0);

  // Determine loan term based on amount
  const calculateTermFromAmount = (amount) => {
    if (amount <= 250) return 2;
    if (amount <= 500) return 3;
    if (amount <= 750) return 4;
    if (amount <= 1000) return 5;
    return 6;
  };

  useEffect(() => {
    calculateLoan();
  }, [loanAmount, paymentFrequency]);

  const calculateLoan = () => {
    const principal = parseFloat(loanAmount);

    // Determine term based on loan amount
    const months = calculateTermFromAmount(principal);
    setLoanTerm(months);

    // Calculate number of payments based on frequency
    let numPayments;
    let periodicRate;

    if (paymentFrequency === 'weekly') {
      numPayments = months * 4; // Exactly 4 weeks per month
      // Calculate periodic rate that gives ~63.5% total cost over the term
      // Using effective rate that produces the correct payment amounts
      periodicRate = 0.084839; // ~8.48% per week
    } else {
      numPayments = months * 2; // Exactly 2 bi-weekly periods per month
      // Using effective rate that produces the correct payment amounts
      periodicRate = 0.161621; // ~16.16% per bi-weekly period
    }

    setNumberOfPayments(numPayments);

    // Payment formula: P * [r(1+r)^n] / [(1+r)^n - 1]
    const periodicPayment =
      (principal * periodicRate * Math.pow(1 + periodicRate, numPayments)) /
      (Math.pow(1 + periodicRate, numPayments) - 1);

    setPayment(periodicPayment.toFixed(2));
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card className="border-2 border-[#00E676]/30 shadow-xl bg-[#0a3b80]">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold text-white">{t('calculator.cardTitle')}</CardTitle>
          <CardDescription className="text-base text-gray-200">{t('calculator.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Loan Amount Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold flex items-center gap-2 text-white">
                <DollarSign className="w-5 h-5 text-[#00E676]" />
                {t('calculator.loanAmount')}
              </Label>
              <span className="text-2xl font-bold text-[#00E676]">${loanAmount}</span>
            </div>
            <Slider
              value={[loanAmount]}
              onValueChange={(value) => setLoanAmount(value[0])}
              min={250}
              max={1500}
              step={50}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-300">
              <span>$250</span>
              <span>$1,500</span>
            </div>
          </div>

          {/* Payment Frequency Selector */}
          <div className="space-y-4">
            <Label className="text-base font-semibold flex items-center gap-2 text-white">
              <Clock className="w-5 h-5 text-[#00E676]" />
              {t('calculator.paymentFrequency')}
            </Label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setPaymentFrequency('weekly')}
                className={`px-6 py-4 rounded-lg font-semibold transition-all duration-200 ${
                  paymentFrequency === 'weekly'
                    ? 'bg-[#00E676] text-[#0D47A1] shadow-lg scale-105'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {t('calculator.weekly')}
              </button>
              <button
                onClick={() => setPaymentFrequency('biweekly')}
                className={`px-6 py-4 rounded-lg font-semibold transition-all duration-200 ${
                  paymentFrequency === 'biweekly'
                    ? 'bg-[#00E676] text-[#0D47A1] shadow-lg scale-105'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {t('calculator.biweekly')}
              </button>
            </div>
          </div>

          {/* Loan Term Display (Auto-calculated) */}
          <div className="space-y-2 bg-white/5 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold flex items-center gap-2 text-white">
                <Calendar className="w-5 h-5 text-[#00E676]" />
                {t('calculator.repaymentPeriod')}
              </Label>
              <span className="text-xl font-bold text-[#FFD600]">
                {loanTerm} {t('calculator.months')} ({numberOfPayments} {t('calculator.payments')})
              </span>
            </div>
          </div>

          {/* Interest Rate Display */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2 text-white">
              <Percent className="w-5 h-5 text-[#00E676]" />
              {t('calculator.interestRate')}
            </Label>
            <div className="text-2xl font-bold text-[#00E676]">{interestRate}% {t('calculator.interestRateUnit')}</div>
          </div>

          {/* Results */}
          <div className="pt-6 border-t border-[#00E676]/30">
            <div className="bg-gradient-to-br from-[#00E676]/20 to-[#FFD600]/20 p-6 rounded-lg text-center border border-[#00E676]/30">
              <p className="text-sm text-gray-200 mb-2">
                {paymentFrequency === 'weekly' ? t('calculator.weeklyPayment') : t('calculator.biweeklyPayment')}
              </p>
              <p className="text-3xl font-bold text-[#00E676]">${payment}</p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center pt-4">
            <Link to={i18n.language === 'fr' ? '/demandez' : '/apply'} className="inline-block bg-[#FF5722] hover:bg-[#f4511e] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg">
              {t('calculator.applyNow')}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoanCalculator;
